import React, { useRef, useMemo, useCallback } from "react";
import JoditEditor, { IJoditEditorProps, Jodit } from "jodit-react";
import { styled, useTheme } from "@mui/material";
import { JODIT_BASE_CONFIG, JODIT_BUTTONS } from "./configs/jodit-config";
import { JODIT_LINK_TEMPLATE } from "./configs/jodit-link-template";

export interface MxRichEditorProps {
  height?: string;
  formRef?: any;
  innerRef?: React.MutableRefObject<Jodit | undefined>;
  placeholder?: string;
  value?: string;
  error?: boolean;
  disabled?: boolean;
  onChange?: (value: {
    target: {
      name: string;
      value: string;
    };
  }) => void;
  onBlur?: (value: {
    target: {
      name: string;
      value: string;
    };
  }) => void;
  name: string;
  id?: string;
}

const MxRichEditor = React.memo(function MxRichEditor(props: MxRichEditorProps) {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const buttons = useMemo(() => [...JODIT_BUTTONS], []);

  const config = useMemo<Partial<IJoditEditorProps["config"]>>(
    () => ({
      language: "es",
      style: {
        fontFamily: theme.typography.fontFamily,
        fontSize: 12,
        fontWeight: "500",
        letterSpacing: "0.05em",
        color: theme.palette.grey[600],
        width: "100%",
        ...(props.error && {
          border: `1px solid ${theme.palette.error.main}`
        })
      },
      readonly: false,
      height: props.height || 500,
      placeholder: props.placeholder || "",
      toolbar: true,
      toolbarSticky: true,
      toolbarButtonSize: "middle",
      buttons,
      buttonsXS: buttons,
      buttonsSM: buttons,
      buttonsMD: buttons,
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: false,
      useSplitMode: false,
      allowResizeY: false,
      askBeforePasteHTML: false,
      allowResizeX: false,
      addNewLine: false,
      safePluginsList: ["table"],
      disabled: props.disabled,
      link: {
        formTemplate: JODIT_LINK_TEMPLATE
      },
      disablePlugins: [
        "poweredByJodit",
        "video",
        "image",
        "file",
        "print",
        "fullsize",
        "source",
        "about",
        "resize",
        "speech-recognize"
      ],
      events: {
        afterInit: (editor: Jodit) => {
          if (props.formRef) {
            // props.formRef?.(editor);
          }

          if (props.innerRef) {
            props.innerRef.current = editor;
          }
        }
      }
    }),
    [
      theme,
      props.placeholder,
      props.disabled,
      props.error,
      props.height,
      buttons,
      props.formRef,
      props.innerRef
    ]
  );

  const handleOnBlur = useCallback(
    (content: string) => {
      const eventData = {
        target: {
          name: props.name,
          value: content
        }
      };

      // Solo llamar onChange si hay cambio
      if (props.onChange) {
        props.onChange(eventData);
      }

      // onBlur solo cuando realmente pierde foco
      if (props.onBlur) {
        props.onBlur(eventData);
      }
    },
    [props.name, props.onChange, props.onBlur]
  );

  return (
    <Container
      error={props.error}
      data-testid={`${props?.id || props.name}-text-quill-field`}
    >
      <JoditEditor
        config={config}
        onBlur={handleOnBlur}
        value={props?.value || ""}
        data-testid={`${props?.id || props.name}-text-quill-field-editor`}
      />
    </Container>
  );
});

export default MxRichEditor;
const Container = styled("div", {
  shouldForwardProp: prop => prop !== "error"
})<{ error?: boolean }>(({ theme, error }) => ({
  width: "100%",
  "& .jodit-container": {
    borderRadius: "20px !important",
    borderColor: error ? `${theme.palette.error.main} !important` : undefined,
    fontSize: 12
  },
  "& .jodit-toolbar__box": {
    borderTopLeftRadius: "20px !important",
    borderTopRightRadius: "20px !important",
    borderColor: error ? `${theme.palette.error.main} !important` : undefined
  },

  "& .jodit-status-bar": {
    borderBottomLeftRadius: "20px !important",
    borderBottomRightRadius: "20px !important",
    paddingRight: "12px",
    "& > *": {
      margin: "0 12px"
    },
    borderColor: error ? `${theme.palette.error.main} !important` : undefined
  }
}));
